import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { vehicleApi } from 'api';
import { refreshUserThunk } from 'actions/userActions';
import { AppDispatch } from 'store/store';
import { getUser } from 'store/selectors/auth.selector';
import { IUser } from 'modules/core/types';
import { setAutoInventoryImportLoading } from 'store/reducers/ui.reducer';

const POLL_MS = 500;
const MAX_POLL_MS = 5 * 60 * 1000;
const SUCCESS_STATUS = 'SUCCESS';
const TERMINAL = new Set([SUCCESS_STATUS, 'FAILURE', 'REVOKED', 'REJECTED', 'IMPORT_FAILED']);

let importInFlight = false;

export const useAutoLoadInventory = (tableSubmitCallback?: VoidFunction) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUser) as IUser;
  const callbackRef = useRef(tableSubmitCallback);

  callbackRef.current = tableSubmitCallback;

  useEffect(() => {
    const eligible = !!user?.foureyesWebsiteId && !user.isWithInventory && !user.withWholesaleInventory;

    if (!eligible || importInFlight) return;

    importInFlight = true;
    dispatch(setAutoInventoryImportLoading(true));

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    const clearPoll = () => {
      if (pollTimer !== undefined) {
        clearTimeout(pollTimer);
        pollTimer = undefined;
      }
    };

    const releaseFlag = () => {
      importInFlight = false;
    };

    const finish = () => {
      releaseFlag();
      dispatch(setAutoInventoryImportLoading(false));
    };

    const pollOnce = async (taskId: string, start: number): Promise<boolean> => {
      if (cancelled) return false;

      if (Date.now() - start > MAX_POLL_MS) {
        finish();

        return false;
      }

      try {
        const { data } = await vehicleApi.getInventoryImportStatus(taskId);

        if (cancelled) return false;

        if (TERMINAL.has(data.status)) {
          if (data.status === SUCCESS_STATUS) {
            callbackRef.current?.();
            void dispatch(refreshUserThunk());
          }

          finish();

          return false;
        }
      } catch {
        if (!cancelled) finish();

        return false;
      }

      return true;
    };

    const schedulePoll = (taskId: string, start: number) => {
      pollTimer = setTimeout(async () => {
        if (cancelled) return;

        const shouldContinue = await pollOnce(taskId, start);

        if (shouldContinue && !cancelled) schedulePoll(taskId, start);
      }, POLL_MS);
    };

    const runImport = async () => {
      try {
        const { data } = await vehicleApi.postFourEyesImport();

        if (cancelled) return;

        if (!data.task_id) {
          finish();

          return;
        }

        const pollStart = Date.now();
        const shouldContinue = await pollOnce(data.task_id, pollStart);

        if (shouldContinue && !cancelled) schedulePoll(data.task_id, pollStart);
      } catch {
        if (!cancelled) finish();
      }
    };

    void runImport();

    return () => {
      cancelled = true;
      clearPoll();
      releaseFlag();
      dispatch(setAutoInventoryImportLoading(false));
    };
  }, [dispatch, user?.foureyesWebsiteId, user?.isWithInventory, user?.withWholesaleInventory]);
};
